import { useEffect, useState } from "react";
import { useRegionalEnrichmentStatus, progressFromRow } from "../lib/useRegionalEnrichmentStatus";
import { findMetro } from "../data/usMetros";
import { FONT } from "../lib/tokens";

const BLUE = "#7eb8d4";

// Keyframes scoped via a unique class so the animation plays once
// per mount even when multiple regions render side-by-side.
const ANIM_CSS = `
@keyframes regionCheckCircle {
  from { stroke-dashoffset: 88; opacity: 0.35; }
  to   { stroke-dashoffset: 0;  opacity: 1; }
}
@keyframes regionCheckMark {
  0%   { stroke-dashoffset: 26; opacity: 0; }
  35%  { stroke-dashoffset: 26; opacity: 0; }
  100% { stroke-dashoffset: 0;  opacity: 1; }
}
@keyframes regionCompleteLabel {
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0); }
}
`;

export default function RegionCompleteStatus({ regionKey }) {
  const row = useRegionalEnrichmentStatus(regionKey);
  const progress = progressFromRow(row);
  // Render only on completion — in-progress / queued / errored are
  // handled inline in CityPicker so the user doesn't see two status
  // surfaces fighting for attention.
  const isDone = progress?.status === "done";

  // Re-trigger the draw-in animation each time `regionKey` mounts —
  // avoids the "static checkmark on Settings re-open" feel.
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => {
    if (isDone) setAnimKey((k) => k + 1);
  }, [isDone, regionKey]);

  if (!isDone) return null;

  const metro = findMetro(regionKey);
  const displayName =
    metro?.label || progress.displayName || regionKey;

  return (
    <>
      <style>{ANIM_CSS}</style>
      <div
        key={animKey}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 0",
          marginBottom: 10,
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 32 32"
          fill="none"
          aria-hidden
        >
          <circle
            cx="16"
            cy="16"
            r="14"
            stroke={BLUE}
            strokeWidth="2"
            strokeDasharray="88"
            style={{
              animation: "regionCheckCircle 600ms ease-out forwards",
            }}
          />
          <path
            d="M9.5 16.5 l4.5 4.5 l8.5 -9.5"
            stroke={BLUE}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray="26"
            style={{
              animation: "regionCheckMark 850ms ease-out forwards",
            }}
          />
        </svg>
        <div
          style={{
            fontFamily: FONT.forma,
            fontStyle: "italic",
            fontWeight: 600,
            fontSize: 13,
            color: BLUE,
            letterSpacing: "0.01em",
            animation:
              "regionCompleteLabel 650ms ease-out 220ms backwards",
          }}
        >
          {displayName} Metropolitan Area Complete!
        </div>
      </div>
    </>
  );
}
